<template>
  <section v-if="tasks.length" class="tk-tray" :class="{ 'tk-tray--open': reviewing }" data-test="tasks-overdue-tray">
    <div class="tk-tray__bar">
      <span class="tk-tray__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16" class="tk-tray__svg" focusable="false">
          <path d="M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18" />
          <path d="M12 7v5l3 2" />
        </svg>
      </span>
      <div class="tk-tray__text">
        <div class="tk-tray__title" data-test="tasks-overdue-title">{{ title }}</div>
        <div class="tk-tray__sub" data-test="tasks-overdue-sub">{{ subtitle }}</div>
      </div>
      <div class="tk-tray__actions">
        <button
          type="button"
          class="tk-tray__btn tk-tray__btn--ghost"
          :disabled="moving || !movableCount"
          :title="movableCount ? null : 'Repeating tasks move with their series. Open one to change its date.'"
          data-test="tasks-overdue-move-all"
          @click="confirmOpen = true"
        >{{ moving ? 'Moving…' : 'Move all to today' }}</button>
        <button
          type="button"
          class="tk-tray__btn"
          :aria-expanded="reviewing ? 'true' : 'false'"
          data-test="tasks-overdue-review"
          @click="reviewing = !reviewing"
        >
          {{ reviewing ? 'Hide' : 'Review' }}
          <svg viewBox="0 0 24 24" width="14" height="14" class="tk-tray__svg" aria-hidden="true" focusable="false">
            <path :d="reviewing ? 'm18 15-6-6-6 6' : 'm6 9 6 6 6-6'" />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="reviewing" class="tk-tray__list" data-test="tasks-overdue-list">
      <task-row
        v-for="t in tasks"
        :key="t.id"
        :task="t"
        :ctx="ctx"
        :employees="employees"
        show-due
        hide-self
        :movable="!moving && movableIds.indexOf(t.id) > -1"
        :complete-delay="completeDelay"
        @open="$emit('open', $event)"
        @complete="$emit('complete', $event)"
        @move="$emit('move', $event)"
      />
    </div>

    <b-modal
      v-model="confirmOpen"
      centered
      :title="confirmTitle"
      ok-title="Move to today"
      cancel-title="Cancel"
      ok-variant="success"
      cancel-variant="light"
      data-test="tasks-overdue-confirm"
      @ok="$emit('move-all')"
    >
      <p class="tk-tray__confirm" data-test="tasks-overdue-confirm-body">{{ confirmBody }}</p>
      <p v-if="repeatingCount" class="tk-tray__confirm" data-test="tasks-overdue-confirm-skipped">{{ skippedText }}</p>
    </b-modal>
  </section>
</template>

<script>
/**
 * OverdueTray — My day's catch-up tray (design "Tasks 2a", Phase B1, 2026-10-06): "N overdue
 * tasks · Oldest from Sep 22 · not counted in today's progress", **Move all to today** (behind a
 * confirm) and **Review** (the overdue rows, inline, each with its own "Move to today").
 *
 * `tasks` = myDay.js sections().overdue (oldest first). `movableIds` = the ids
 * moveAllToTodayPatch() would move (repeating tasks are left out — moving one re-anchors its whole
 * series); the confirm says how many repeating tasks it skips. Emits `move-all` only after the
 * confirm's OK; the PAGE dispatches the patches (one tasks/updateTask each). Also `open(task)`,
 * `complete(change)`, `move(task)`. No store access of its own (TaskRow completes its task).
 */
import TaskRow from './TaskRow.vue';
import { dueDayOf } from '@/utils/tasks/myDay.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shortDate(ymd, today) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  if (!m) return '';
  const label = `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
  return String(today || '').slice(0, 4) === m[1] ? label : `${label}, ${m[1]}`;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default {
  name: 'OverdueTray',
  components: { TaskRow },
  props: {
    tasks: { type: Array, default: () => [] },
    ctx: { type: Object, required: true },
    employees: { type: Array, default: () => [] },
    movableIds: { type: Array, default: () => [] },
    moving: { type: Boolean, default: false },
    completeDelay: { type: Number, default: 1200 },
  },

  data() {
    return { reviewing: false, confirmOpen: false };
  },

  computed: {
    title() {
      return plural(this.tasks.length, 'overdue task', 'overdue tasks');
    },
    oldest() {
      for (let i = 0; i < this.tasks.length; i += 1) {
        const d = dueDayOf(this.tasks[i], this.ctx);
        if (d) return shortDate(d, this.ctx.today);
      }
      return '';
    },
    subtitle() {
      return this.oldest
        ? `Oldest from ${this.oldest} · not counted in today’s progress`
        : 'Not counted in today’s progress';
    },
    movableCount() {
      return this.movableIds.length;
    },
    repeatingCount() {
      return Math.max(0, this.tasks.length - this.movableCount);
    },
    confirmTitle() {
      return `Move ${plural(this.movableCount, 'task', 'tasks')} to today?`;
    },
    confirmBody() {
      return this.movableCount === 1
        ? 'Its due date becomes today, so it counts in today’s progress.'
        : 'Their due date becomes today, so they count in today’s progress.';
    },
    skippedText() {
      return `${plural(this.repeatingCount, 'repeating task stays', 'repeating tasks stay')} where ${this.repeatingCount === 1 ? 'it is' : 'they are'}: moving one would shift its whole series.`;
    },
  },

  watch: {
    // Nothing left to review → close the list, so a new overdue task does not reopen it.
    tasks(list) {
      if (!list.length) this.reviewing = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.tk-tray {
  margin-top: $fu-space-5;
  border: 1px solid $fu-border;
  border-radius: $fu-radius-lg;
  background: $fu-neutral-25;
}

.tk-tray__bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $fu-space-3;
  padding: 14px 16px;
}

.tk-tray__icon {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: $fu-radius-sm + 2px;
  background: $fu-danger-subtle;
  color: $fu-danger-text;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tk-tray__svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}

.tk-tray__text {
  flex: 1 1 220px;
  min-width: 0;
}

.tk-tray__title {
  font-size: $fu-fs-body-sm;
  font-weight: $fu-fw-bold;
  color: $fu-text;
}

.tk-tray__sub {
  margin-top: 2px;
  font-size: 12.5px;
  color: $fu-text-muted;
}

.tk-tray__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $fu-space-2;
  margin-left: auto;
}

.tk-tray__btn {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: $fu-radius-md;
  border: 1px solid $fu-border-strong;
  background: $fu-surface;
  font-size: 13.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  white-space: nowrap;
  &:hover:not([disabled]) { background: $fu-neutral-50; }
  &[disabled] { opacity: 0.55; cursor: not-allowed; }
}

.tk-tray__btn--ghost {
  border-color: transparent;
  background: transparent;
  &:hover:not([disabled]) { background: $fu-neutral-100; }
}

.tk-tray__list {
  border-top: 1px solid $fu-border;
  padding: 6px 4px;
}

.tk-tray__confirm {
  margin: 0 0 8px;
  font-size: $fu-fs-body-sm;
  color: $fu-text-strong;
}

@include fu-phone {
  .tk-tray__bar { padding: 12px; }
  .tk-tray__actions { margin-left: 44px; }
}
</style>
